// ═══════════════════════════════════════════════════════════════════════════
// PAGE ADMIN GROUPS — invitation depuis la modale utilisateur (câblage)
// Extrait de page-admin-groups.ts (`showUserModal` à 245 lignes, à 5 du
// cliquet taille-fonction, 2026-09-27) : les trois boutons du bloc
// `um-invite-group` — générer le lien, envoyer le mail, copier le lien.
// Le markup reste dans page-user-modal-fragments ; ici, seuls les écouteurs.
// Comportement identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : api, t, toast, safeWarn.

import type { UserEntityFull } from './page-admin-groups.js';

interface InviteUserResp {
  url: string;
}

/**
 * Câble les boutons d'invitation de la modale utilisateur.
 *
 * Générer et Envoyer ne s'attachent que pour un utilisateur existant qui n'est
 * pas une équipe (`user` null à la création : le bloc est absent du markup).
 * Copier s'attache dès que le bouton existe. À appeler une fois la modale
 * montée dans le DOM.
 */
export function wireInvitationButtons(user: UserEntityFull | null): void {
  const invBtn = document.getElementById('um-invite-btn') as HTMLButtonElement | null;
  if (invBtn && user && !user.is_team) {
    invBtn.addEventListener('click', () => {
      void (async () => {
        invBtn.disabled = true;
        invBtn.textContent = t('user_modal.invite.btn.generating');
        try {
          const res = (await api.inviteUser(user.id)) as InviteUserResp;
          const resultDiv = document.getElementById('um-invite-result');
          const urlInput = document.getElementById('um-invite-url') as HTMLInputElement | null;
          if (urlInput) {
            urlInput.value = res.url;
            urlInput.focus();
            urlInput.select();
          }
          if (resultDiv) resultDiv.style.display = '';
          toast(t('user_modal.invite.toast.generated'), 'success');
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('user_modal.invite.error.generate');
          toast(msg, 'error');
        } finally {
          invBtn.disabled = false;
          invBtn.textContent = t('user_modal.invite.btn.generate');
        }
      })();
    });
  }

  // « Envoyer une invitation » : Ornexia écrit lui-même au destinataire, à
  // l'adresse de sa fiche. Le backend contrôle l'adresse et remonte l'échec
  // — l'administrateur doit savoir si le message est parti.
  const sendBtn = document.getElementById('um-invite-send-btn') as HTMLButtonElement | null;
  if (sendBtn && user && !user.is_team) {
    sendBtn.addEventListener('click', () => {
      void (async () => {
        sendBtn.disabled = true;
        sendBtn.textContent = t('user_modal.invite.btn.sending');
        try {
          const res = (await api.sendUserInvitation(user.id)) as { sent_to?: string };
          toast(t('user_modal.invite.toast.sent', { email: res.sent_to || '' }), 'success');
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('user_modal.invite.error.send');
          toast(msg, 'error');
        } finally {
          sendBtn.disabled = false;
          sendBtn.textContent = t('user_modal.invite.btn.send');
        }
      })();
    });
  }

  const copyBtn = document.getElementById('um-invite-copy');
  if (copyBtn) {
    copyBtn.addEventListener('click', () => {
      void (async () => {
        const urlInput = document.getElementById('um-invite-url') as HTMLInputElement | null;
        if (!urlInput || !urlInput.value) return;
        try {
          await navigator.clipboard.writeText(urlInput.value);
          toast(t('user_modal.invite.toast.copied'), 'success');
        } catch (e: unknown) {
          safeWarn('clipboard.writeText (fallback execCommand)', e);
          urlInput.select();
          document.execCommand('copy');
          toast(t('user_modal.invite.toast.copied_short'), 'success');
        }
      })();
    });
  }
}
